import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { updatePatientCreation } from '../../../services/patient/creation';
import Loading from '../../../components/Loading';
import VideoPlayerModal from '../../../components/modals/VideoPlayerModal';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import { reportCaughtError } from '../../../services/errorReporting';
import { CaseTypeCards } from './components/CaseTypeCards';
import { CaseTypeTable } from './components/CaseTypeTable';
import { IntensityKey } from './components/IntensityDots';
import { TreatmentDetails, treatmentDetails } from './treatmentDetails';

interface VideoModalState {
    url: string;
    title: string;
    description: string;
}

const CaseType: React.FC = () => {
    const { formData, updateSubmissionForm } = useSubmissionForm();
    const { goNext } = useStepperContext();
    const { setFooterContent } = useLayoutFooter();

    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [videoModal, setVideoModal] = useState<VideoModalState | null>(null);
    const [isModified, setIsModified] = useState(false);

    const caseTypes = useMemo(() => Object.values(treatmentDetails), []);
    const selectedCaseType = formData?.caseType as TreatmentDetails | undefined;

    const handleSelect = useCallback(
        (caseType: TreatmentDetails) => {
            setIsModified(true);
            updateSubmissionForm({ caseType });
        },
        [updateSubmissionForm]
    );

    const handleLearnMore = useCallback((caseType: TreatmentDetails) => {
        setVideoModal({
            url: caseType.learnMoreLink,
            title: `Learn more about ${caseType.label}`,
            description: caseType.description
        });
    }, []);

    const handleNext = useCallback(async () => {
        if (!selectedCaseType) return;

        setIsLoading(true);
        try {
            if (isModified && formData?.id) {
                await updatePatientCreation(formData.id, {
                    caseType: selectedCaseType.monday_key
                });
            }
            goNext();
        } catch (error) {
            showToast('Failed to save case type. Please try again.', 'error');
            reportCaughtError(error);
        } finally {
            setIsLoading(false);
        }
    }, [goNext, isModified, formData?.id, selectedCaseType, showToast]);

    // Sync initial form data
    useEffect(() => {
        if (!formData?.caseType && selectedCaseType) {
            updateSubmissionForm({ caseType: selectedCaseType });
        }
    }, [formData?.caseType, updateSubmissionForm, selectedCaseType]);

    // Footer content
    useEffect(() => {
        setFooterContent(
            <div className="w-full flex justify-between items-center gap-4">
                <p className="text-sm text-gray-700 italic max-w-4xl">
                    * Orthodontic treatment with clear aligners is intended for tooth movement and
                    bite correction. While airway health and joint function are considered in
                    treatment planning, aligners are not currently indicated for airway disorders or
                    temporomandibular joint disease (TMD). Treatment plans may support broader
                    health outcomes when coordinated with other healthcare providers.
                </p>
                <div className="shrink-0 flex items-center gap-3">
                    <MessageOdoFooterButton />
                    <button
                        disabled={isLoading || !selectedCaseType}
                        onClick={handleNext}
                        className={`shrink-0 px-4 py-2.5 rounded-lg font-bold text-sm tracking-wider transition-colors ${
                            isLoading || !selectedCaseType
                                ? 'bg-gray-300 text-white cursor-not-allowed'
                                : 'bg-gray-800 text-white hover:bg-gray-700'
                        }`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        );

        return () => setFooterContent(null);
    }, [setFooterContent, isLoading, handleNext, selectedCaseType]);

    return (
        <>
            {isLoading && <Loading text="Saving..." />}

            <div className="flex flex-col items-center w-full">
                <h1 className="text-2xl font-bold text-gray-900 mb-8">
                    Choose your ODO powered mechanics
                </h1>

                <div className="w-full max-w-[90%] space-y-6">
                    <CaseTypeTable
                        caseTypes={caseTypes}
                        selectedKey={selectedCaseType?.key}
                        onSelect={handleSelect}
                        onLearnMore={handleLearnMore}
                    />

                    <IntensityKey />
                </div>

                <hr className="w-full max-w-[90%] border-gray-200 my-16" />

                <div className="w-full max-w-[90%]">
                    <CaseTypeCards caseTypes={caseTypes} onLearnMore={handleLearnMore} />
                </div>
            </div>

            {videoModal && (
                <VideoPlayerModal
                    isOpen={true}
                    onClose={() => setVideoModal(null)}
                    videoUrl={videoModal.url}
                    title={videoModal.title}
                    description={videoModal.description}
                />
            )}
        </>
    );
};

export default CaseType;
